<script setup lang="ts">
import { ref } from "vue";
import Button from "../../components/Button.vue";
import { useModalStore } from "../../stores/modalStore.ts";
import type {
  CapacityRecipientPreview,
  CapacityWarningChoice,
} from "./shippingPoEditing.ts";

const props = defineProps<{
  sku: string;
  poSequence: number;
  proposedQty: number;
  maxFittingQty: number;
  redistributionPreview: CapacityRecipientPreview[];
  unallocatedQty: number;
  remainingAfterRedistribution: number;
  onConfirm: (choice: CapacityWarningChoice) => void;
  onCancel: () => void;
}>();

const modalStore = useModalStore();
const selection = ref<CapacityWarningChoice["type"]>("fit");
const manualQuantity = ref(String(props.maxFittingQty));
const manualError = ref("");

function handleOk(): void {
  if (selection.value === "manual") {
    const quantity = Number(manualQuantity.value);
    if (
      manualQuantity.value === "" ||
      !Number.isInteger(quantity) ||
      quantity < 0 ||
      quantity > props.maxFittingQty
    ) {
      manualError.value = `Enter a whole quantity from 0 to ${String(props.maxFittingQty)}.`;
      return;
    }
    modalStore.close();
    props.onConfirm({ type: "manual", quantity });
    return;
  }
  modalStore.close();
  props.onConfirm({ type: selection.value });
}

function handleCancel(): void {
  props.onCancel();
  modalStore.close();
}
</script>

<template>
  <div class="capacity-warning-modal">
    <div class="modal-header draggable">
      <span class="modal-header-title">PO cube capacity exceeded</span>
      <button
        type="button"
        class="modal-header-close"
        aria-label="Close capacity warning"
        @mousedown.stop
        @click.stop="handleCancel"
      >
        &times;
      </button>
    </div>
    <div class="modal-body capacity-warning-body">
      <p class="capacity-warning-summary">
        Adding SKU {{ sku }} at quantity {{ proposedQty }} would exceed the cube
        capacity of PO {{ poSequence }}. The maximum quantity that fits is
        <strong>{{ maxFittingQty }}</strong
        >.
      </p>

      <fieldset class="capacity-warning-options">
        <legend>Choose how to continue</legend>
        <label class="capacity-warning-option" for="capacity-choice-fit">
          <input
            id="capacity-choice-fit"
            v-model="selection"
            type="radio"
            name="capacity-choice"
            value="fit"
          />
          Add the maximum that fits; leave the rest in leftovers
        </label>
        <label class="capacity-warning-option" for="capacity-choice-override">
          <input
            id="capacity-choice-override"
            v-model="selection"
            type="radio"
            name="capacity-choice"
            value="override"
          />
          Override and proceed over capacity
        </label>
        <label class="capacity-warning-option" for="capacity-choice-remove">
          <input
            id="capacity-choice-remove"
            v-model="selection"
            type="radio"
            name="capacity-choice"
            value="remove"
          />
          Remove this SKU from PO {{ poSequence }}
        </label>
        <label class="capacity-warning-option" for="capacity-choice-manual">
          <input
            id="capacity-choice-manual"
            v-model="selection"
            type="radio"
            name="capacity-choice"
            value="manual"
          />
          Enter a different quantity (up to {{ maxFittingQty }})
        </label>
        <div v-if="selection === 'manual'" class="capacity-warning-manual">
          <span id="capacity-warning-quantity-label">Quantity</span>
          <input
            id="capacity-warning-quantity"
            v-model="manualQuantity"
            aria-labelledby="capacity-warning-quantity-label"
            type="number"
            min="0"
            :max="maxFittingQty"
            step="1"
            @input="manualError = ''"
          />
          <p v-if="manualError" class="capacity-warning-error" role="alert">
            {{ manualError }}
          </p>
        </div>
        <label
          class="capacity-warning-option"
          for="capacity-choice-redistribute"
        >
          <input
            id="capacity-choice-redistribute"
            v-model="selection"
            type="radio"
            name="capacity-choice"
            value="redistribute"
          />
          Add the maximum that fits, then redistribute all remaining units
        </label>
      </fieldset>

      <div
        v-if="selection === 'redistribute'"
        class="capacity-warning-preview"
        aria-live="polite"
      >
        <h3>Recipient preview</h3>
        <p>
          {{ unallocatedQty }} unit{{ unallocatedQty === 1 ? "" : "s" }} will be
          considered for redistribution.
        </p>
        <ul v-if="redistributionPreview.length > 0">
          <li
            v-for="candidate in redistributionPreview"
            :key="candidate.poSequence"
          >
            PO {{ candidate.poSequence }}: {{ candidate.additionalQty }} planned
            ({{ candidate.maxAdditionalQty }} unit{{
              candidate.maxAdditionalQty === 1 ? "" : "s"
            }}
            of cube room<span v-if="!candidate.hasSkuLine"
              >; SKU line will be added</span
            >)
          </li>
        </ul>
        <p v-else>No other existing POs currently have cube room.</p>
        <p>
          {{ remainingAfterRedistribution }} unit{{
            remainingAfterRedistribution === 1 ? "" : "s"
          }}
          will remain in leftovers after this distribution.
        </p>
      </div>
    </div>
    <div class="modal-footer capacity-warning-footer">
      <Button variant="secondary" @click="handleCancel">Cancel</Button>
      <Button variant="primary" @click="handleOk">Apply choice</Button>
    </div>
  </div>
</template>

<style scoped>
.capacity-warning-modal {
  display: flex;
  height: 100%;
  flex-direction: column;
}

.capacity-warning-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
}

.capacity-warning-summary {
  margin: 0 0 0.8rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
}

.capacity-warning-options {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  border: 0;
  padding: 0;
}

.capacity-warning-options legend {
  margin-bottom: 0.45rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
  font-weight: 700;
}

.capacity-warning-option {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: var(--text-primary, #172033);
  font-size: 0.82rem;
}

.capacity-warning-option input {
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.capacity-warning-manual {
  display: grid;
  grid-template-columns: auto minmax(5rem, 8rem);
  align-items: center;
  gap: 0.45rem;
  margin-left: 1.4rem;
}

.capacity-warning-manual input {
  width: 100%;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
}

.capacity-warning-error {
  grid-column: 1 / -1;
  margin: 0;
  color: var(--danger, #b42318);
  font-size: 0.78rem;
}

.capacity-warning-preview {
  margin-top: 0.9rem;
  border-top: 1px solid var(--border-subtle, #d8dee8);
  padding-top: 0.75rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.8rem;
}

.capacity-warning-preview h3 {
  margin: 0 0 0.35rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
}

.capacity-warning-preview p {
  margin: 0.35rem 0;
}

.capacity-warning-preview ul {
  margin: 0.35rem 0;
  padding-left: 1.1rem;
}

.capacity-warning-footer {
  flex: 0 0 auto;
}
</style>
